import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getdb, deleteProduct, FilterOptions } from '@/services/dbService';
import { ShoeItem } from '@/models/types';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationInfo,
} from '@/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import FilterPanel from '@/components/inventory/FilterPanel';
import { toast } from 'sonner';
import { Search, Edit, Trash2, Plus, Footprints, FilterX, Link2, GripVertical, Save } from 'lucide-react';
import { bulkUpdateProductOrders, updateProductOrder } from '@/services/productsService';
import { useLanguage } from '@/i18n';

const ShoesList = () => {
  const [items, setItems] = useState<ShoeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [filters, setFilters] = useState<FilterOptions>({});
  const [sortOption, setSortOption] = useState<string>('');
  const [activeFilterCount, setActiveFilterCount] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isPageJumpOpen, setIsPageJumpOpen] = useState(false);
  const [pageJumpValue, setPageJumpValue] = useState("");
  const [reorderMode, setReorderMode] = useState(false);
  const [hasOrderChanges, setHasOrderChanges] = useState(false);
  const dragFromIndexRef = useRef<number | null>(null);
  const { t } = useLanguage();

  const fetchItems = async (page = 1, applyFilters = filters, perPage = itemsPerPage) => {
    try {
      setLoading(true);
      
      const currentFilters: FilterOptions = { ...applyFilters };
      if (searchTerm) {
        currentFilters.searchTerm = searchTerm;
      }
      
      if (sortOption && (sortOption.startsWith('id_') || sortOption.startsWith('order_'))) {
        currentFilters.sortOption = sortOption;
      }

      const { data, count } = await getdb(page, perPage, currentFilters);
      
      let sortedData = [...data];
      if (sortOption && !sortOption.startsWith('id_')) {
        const [field, direction] = sortOption.split('_');
        sortedData.sort((a, b) => {
          if (field === 'name') {
            return direction === 'asc' 
              ? a.name.localeCompare(b.name)
              : b.name.localeCompare(a.name);
          } else if (field === 'price') {
            return direction === 'asc'
              ? a.price - b.price
              : b.price - a.price;
          }
          return 0;
        });
      }
      
      setItems(sortedData);
      setTotalItems(count);
      setTotalPages(Math.ceil(count / perPage));
      setCurrentPage(page);
    } catch (error) {
      console.error('Error fetching items:', error);
      toast.error('Failed to load items. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(1, filters, itemsPerPage);
  }, [filters, sortOption, itemsPerPage]);
  
  useEffect(() => {
    let count = 0;
    if (filters.category) count++;
    if (filters.brand) count++;
    if (filters.minPrice !== undefined) count++;
    if (filters.maxPrice !== undefined) count++;
    if (filters.itemId !== undefined) count++;
    if (searchTerm) count++;
    setActiveFilterCount(count);
  }, [filters, searchTerm]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchItems(page, filters, itemsPerPage);
  };

  const handleItemsPerPageChange = (value: string) => {
    const newItemsPerPage = parseInt(value);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(1);
  };
  const handleFilterChange = (newFilters: FilterOptions) => {
    const processedFilters: FilterOptions = {};
    
    if (newFilters.category && newFilters.category !== "_all") {
      processedFilters.category = newFilters.category;
    }
    
    if (newFilters.brand && newFilters.brand !== "_all") {
      processedFilters.brand = newFilters.brand;
    }
    
    if (newFilters.minPrice !== undefined) {
      processedFilters.minPrice = newFilters.minPrice;
    }
    
    if (newFilters.maxPrice !== undefined) {
      processedFilters.maxPrice = newFilters.maxPrice;
    }
    
    if (newFilters.itemId !== undefined) {
      processedFilters.itemId = newFilters.itemId;
    }
    
    setFilters(processedFilters);
    setCurrentPage(1);
  };

  const handleSortChange = (sort: string) => {
    setSortOption(sort === "_default" ? "" : sort);
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setFilters({});
    setSortOption('');
    setSearchTerm('');
    setCurrentPage(1);
  };

  const canReorderAll = () => currentPage === 1 && totalItems <= itemsPerPage;

  const toggleReorderMode = () => {
    if (!reorderMode) {
      if (!canReorderAll()) {
        toast.error('Set page to 1 and increase Items per page to include all items before reordering.');
        return;
      }
    }
    setReorderMode((v) => !v);
    setHasOrderChanges(false);
  };

  const onDragStart = (index: number) => {
    dragFromIndexRef.current = index;
  };

  const onDragOver = (e: React.DragEvent) => {
    if (reorderMode) e.preventDefault();
  };

  const onDrop = (toIndex: number) => {
    const fromIndex = dragFromIndexRef.current;
    dragFromIndexRef.current = null;
    if (!reorderMode || fromIndex === null || fromIndex === toIndex) return;
    setItems((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      const reassigned = next.map((it, idx) => ({ ...it, order: idx + 1 }));
      setHasOrderChanges(true);
      return reassigned as ShoeItem[];
    });
  };

  const handleSaveReorder = async () => {
    try {
      const payload = items.map((it, idx) => ({ _id: it._id, order: (it as any).order ?? (idx + 1) }));
      await bulkUpdateProductOrders(payload);
      toast.success('Order updated');
      setHasOrderChanges(false);
      fetchItems(1, filters, itemsPerPage);
    } catch (err) {
      console.error(err);
      toast.error('Failed to save order');
    }
  };

  const handleInlineOrderChange = (index: number, value: string) => {
    const num = value === '' ? undefined : parseInt(value, 10);
    setItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], order: num as any };
      return next;
    });
  };

  const handleInlineOrderBlur = async (itemId: string, value: number | undefined) => {
    if (!value || value < 1) {
      toast.error('Order must be a positive number');
      return;
    }
    try {
      await updateProductOrder(itemId, value);
      toast.success('Order updated');
    } catch (e) {
      console.error(e);
      toast.error('Failed to update order');
    } finally {
      fetchItems(currentPage, filters, itemsPerPage);
    }
  };

  const handleDeleteItem = async (id: string) => {
    try {
      setDeleting(true);
      await deleteProduct(id);
      
      setItems(prevItems => prevItems.filter(item => item._id !== id));
      
      if (items.length === 1 && currentPage > 1) {
        handlePageChange(currentPage - 1);
      } else {
        fetchItems(currentPage);
      }
      
      toast.success('Item deleted successfully');
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Failed to delete item. Please try again later.');
      fetchItems(currentPage);
    } finally {
      setDeleting(false);
    }
  };

  const handlePageJump = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setPageJumpValue("");
      setIsPageJumpOpen(false);
      handlePageChange(page);
    } else {
      toast.error(`Please enter a valid page number (1-${totalPages})`);
    }
  };

  const renderPaginationNumbers = () => {
    const pages = [];
    
    if (totalPages <= 1) {
      pages.push(1);
      return pages;
    }
    
    // Always show page 1
    pages.push(1);
    
    // For pages with many numbers, show 3 on left and 2 on right of current page
    if (totalPages <= 7) {
      // If 7 or fewer pages, show all page numbers
      for (let i = 2; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Calculate the range to display based on current page
      const leftSide = 3; // Show 3 pages on the left
      const rightSide = 2; // Show 2 pages on the right
      
      // If current page is near the beginning
      if (currentPage <= leftSide + 1) {
        // Show first leftSide+1 pages (2 to leftSide+1), then ellipsis, then last page
        for (let i = 2; i <= leftSide + 2; i++) {
          pages.push(i);
        }
        pages.push('ellipsis-end');
        pages.push(totalPages);
      } 
      // If current page is near the end
      else if (currentPage >= totalPages - rightSide) {
        // Show ellipsis, then last rightSide+1 pages
        pages.push('ellipsis-start');
        for (let i = totalPages - (rightSide + leftSide); i < totalPages; i++) {
          pages.push(i);
        }
        pages.push(totalPages);
      } 
      // If current page is in the middle
      else {
        pages.push('ellipsis-start');
        // Show leftSide pages before current page
        for (let i = currentPage - leftSide + 1; i < currentPage; i++) {
          pages.push(i);
        }
        // Show current page
        pages.push(currentPage);
        // Show rightSide pages after current page
        for (let i = currentPage + 1; i <= currentPage + rightSide; i++) {
          if (i < totalPages) {
            pages.push(i);
          }
        }
        
        // Only add end ellipsis if needed
        if (currentPage + rightSide < totalPages - 1) {
          pages.push('ellipsis-end');
          // Only add last page if it's not already included
          if (currentPage + rightSide < totalPages) {
            pages.push(totalPages);
          }
        }
      }
    }
    
    return pages;
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('inventory')}</h1>
          <p className="text-muted-foreground">{t('manageShoes')}</p>
        </div>
        <Link to="/admin/shoes/new">
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            {t('addNewShoe')}
          </Button>
        </Link>
      </div>

      <Card className="bg-background border border-border/50 shadow-subtle">
        <CardHeader className="pb-2">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle>Shoes</CardTitle>
              <CardDescription>
                {loading ? t('loadingShoes') : t('showingShoesOf', { count: items.length, total: totalItems })}
              </CardDescription>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <form onSubmit={handleSearch} className="flex w-full sm:w-auto">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="search"
                    placeholder={t('searchShoes')}
                    className="pl-8 bg-background"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <Button type="submit" variant="ghost" size="icon" className="ml-2">
                  <Search className="h-4 w-4" />
                </Button>
              </form>
              
              <div className="flex gap-2">
                <FilterPanel 
                  onFilterChange={handleFilterChange}
                  onSortChange={handleSortChange}
                  onReset={handleResetFilters}
                  activeFilterCount={activeFilterCount}
                />
                
                {activeFilterCount > 0 && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleResetFilters} 
                    className="flex items-center"
                  >
                    <FilterX className="mr-2 h-4 w-4" />
                    {t('clear')}
                  </Button>
                )}
                <Button variant={reorderMode ? 'default' : 'outline'} size="sm" onClick={toggleReorderMode}>
                  <GripVertical className="mr-2 h-4 w-4" />
                  {reorderMode ? t('reordering') : t('reorder')}
                </Button>
                {reorderMode && hasOrderChanges && (
                  <Button size="sm" onClick={handleSaveReorder}>
                    <Save className="mr-2 h-4 w-4" />
                    {t('saveOrder')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center gap-2">
                <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
                <span className="text-sm text-muted-foreground">{t('loadingShoes')}</span>
              </div>
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 gap-4">
              <div className="w-16 h-16 rounded-full bg-muted/30 flex items-center justify-center">
                <Footprints className="h-8 w-8 text-muted-foreground" />
              </div>
              <div className="text-center">
                <h3 className="text-lg font-medium">{t('noShoesFound')}</h3>
                <p className="text-muted-foreground">
                  {activeFilterCount > 0 
                    ? t('tryDifferentFilters')
                    : t('getStartedAddingShoe')}
                </p>
              </div>
              {activeFilterCount > 0 ? (
                <Button variant="outline" onClick={handleResetFilters}>
                  <FilterX className="mr-2 h-4 w-4" />
                  {t('clearAllFilters')}
                </Button>
              ) : (
                <Link to="/admin/shoes/new">
                  <Button variant="outline">
                    <Plus className="mr-2 h-4 w-4" />
                    {t('addNewShoe')}
                  </Button>
                </Link>
              )}
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('order')}</TableHead>
                    <TableHead>{t('name')}</TableHead>
                    <TableHead className="hidden md:table-cell">{t('brand')}</TableHead>
                    <TableHead className="hidden md:table-cell">{t('category')}</TableHead>
                    <TableHead className="text-right">{t('price')}</TableHead>
                    <TableHead className="text-right">{t('actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item, idx) => (
                    <TableRow
                      key={item._id}
                      draggable={reorderMode}
                      onDragStart={() => onDragStart(idx)}
                      onDragOver={onDragOver}
                      onDrop={() => onDrop(idx)}
                      className={reorderMode ? 'cursor-grab' : ''}
                    >
                      <TableCell className="w-[90px]">
                        <div className="flex items-center gap-2">
                          {reorderMode && <GripVertical className="h-4 w-4 text-muted-foreground" />}
                          <Input
                            type="number"
                            min="1"
                            className="h-8 w-[70px]"
                            value={(item as any).order ?? ''}
                            onChange={(e) => handleInlineOrderChange(idx, e.target.value)}
                            onBlur={(e) => handleInlineOrderBlur(item._id, parseInt(e.target.value, 10))}
                            disabled={reorderMode}
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <a 
                            href={item.link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="w-10 h-10 rounded-md bg-muted flex items-center justify-center overflow-hidden"
                            title="View product"
                          >
                            {item.image ? (
                              <img 
                                src={item.image} 
                                alt={item.name}
                                className="w-full h-full object-cover rounded-md"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = '/placeholder.svg';
                                }}
                              />
                            ) : (
                              <Footprints className="h-5 w-5 text-muted-foreground" />
                            )}
                          </a>
                          <div>
                            <p className="font-medium truncate max-w-[180px]">{item.name}</p>
                            <p className="text-xs text-muted-foreground truncate max-w-[180px]">
                              {item.link}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {item.brand}
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <Badge variant="outline" className="bg-background/50">
                          {item.category}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium">${item.price.toFixed(2)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <a 
                            href={item.link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            aria-label="Visit product page"
                          >
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <Link2 className="h-4 w-4" />
                            </Button>
                          </a>
                          <Link to={`/admin/shoes/${item._id}`}>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <Edit className="h-4 w-4" />
                            </Button>
                          </Link>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-background border border-border/50">
                              <AlertDialogHeader>
                              <AlertDialogTitle>{t('areYouSure')}</AlertDialogTitle>
                                <AlertDialogDescription>
                                {t('deleteItemConfirm', { name: item.name })}
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                              <AlertDialogCancel className="bg-background hover:bg-muted">{t('cancel')}</AlertDialogCancel>
                                <AlertDialogAction
                                  className="bg-destructive text-white hover:bg-destructive/90"
                                  onClick={() => handleDeleteItem(item._id)}
                                  disabled={deleting}
                                >
                                {deleting ? t('deleting') : t('delete')}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {totalPages > 0 && (
            <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>{t('itemsPerPage')}</span>
                <Select value={itemsPerPage.toString()} onValueChange={handleItemsPerPageChange}>
                  <SelectTrigger className="h-8 w-[70px]">
                    <SelectValue placeholder={itemsPerPage.toString()} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="500">500</SelectItem>
                    <SelectItem value="1000">1000</SelectItem>
                  </SelectContent>
                </Select>
                <span>
                  {t('showingShoesOf', { count: items.length, total: totalItems })}
                </span>
              </div>
              
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious 
                      onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                      className={currentPage === 1 ? 'pointer-events-none opacity-50' : ''}
                    />
                  </PaginationItem>
                  
                  <PaginationItem className="hidden xs:block">
                    <PaginationInfo 
                      currentPage={currentPage} 
                      totalPages={totalPages} 
                      totalItems={totalItems}
                      itemsPerPage={itemsPerPage}
                    />
                  </PaginationItem>
                  
                  {renderPaginationNumbers().map((page, i) => (
                    <PaginationItem key={i} className="hidden sm:block">
                      {typeof page === 'number' ? (
                        <PaginationLink 
                          onClick={() => handlePageChange(page)}
                          isActive={currentPage === page}
                        >
                          {page}
                        </PaginationLink>
                      ) : (
                        <Popover open={isPageJumpOpen} onOpenChange={setIsPageJumpOpen}>
                          <PopoverTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-9 w-9">
                              <PaginationEllipsis />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-50 p-0" align="center">
                            <Command>
                              <CommandInput 
                                placeholder={`Page (1-${totalPages})`}
                                value={pageJumpValue}
                                onValueChange={setPageJumpValue}
                              />
                              <CommandList>
                                <CommandEmpty>No page found</CommandEmpty>
                                <CommandGroup>
                                  {Array.from({ length: Math.min(10, totalPages) }, (_, i) => {
                                    let pageNum = 0;
                                    
                                    if (page === 'ellipsis-start') {
                                      // For the start ellipsis, show pages right after page 1
                                      pageNum = 2 + i;
                                    } else if (page === 'ellipsis-end') {
                                      // For the end ellipsis, show pages right before the last page
                                      pageNum = totalPages - 10 + i;
                                    }
                                    
                                    if (pageNum > 1 && pageNum < totalPages) {
                                      return (
                                        <CommandItem
                                          key={pageNum}
                                          value={pageNum.toString()}
                                          onSelect={() => handlePageJump(pageNum)}
                                        >
                                          Page {pageNum}
                                        </CommandItem>
                                      );
                                    }
                                    return null;
                                  }).filter(Boolean)}
                                </CommandGroup>
                                <div className="border-t px-2 py-2">
                                  <form 
                                    onSubmit={(e) => {
                                      e.preventDefault();
                                      const page = parseInt(pageJumpValue);
                                      if (!isNaN(page)) {
                                        handlePageJump(page);
                                      }
                                    }}
                                    className="flex items-center gap-2"
                                  >
                                    <Input
                                      type="number"
                                      min="1"
                                      max={totalPages}
                                      placeholder="Jump to page..."
                                      className="h-8"
                                      value={pageJumpValue}
                                      onChange={(e) => setPageJumpValue(e.target.value)}
                                    />
                                    <Button type="submit" size="sm" className="h-8">Go</Button>
                                  </form>
                                </div>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      )}
                    </PaginationItem>
                  ))}
                  
                  <PaginationItem>
                    <PaginationNext 
                      onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                      className={currentPage === totalPages ? 'pointer-events-none opacity-50' : ''}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ShoesList;
